/**
 * WeeklyReportPdfDocument.tsx — Enterprise Executive PDF for Weekly Impact Report
 * ============================================================================
 * Redesigned to modern enterprise report design standards (Siemens/ABB/Schneider style):
 *   • Cortex logo neatly positioned in top-left corner
 *   • Perfectly centered report title "WEEKLY IMPACT REPORT" + date range below
 *   • Thin Cortex blue divider with equal margins
 *   • Structured 2-column information card with subtle secondary styling for Consumer ID
 *   • Strictly engineered for 1-page A4 print layout
 */

import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import type { WeeklyReportPayload } from '@/lib/reports/buildWeeklyReportPayload';
import cortexHeaderLogo from '@/assets/cortex_header_logo.png';
import vireonWatermark from '@/assets/vireon_watermark.png';

export interface RenderedSectionForPdf {
  title: string;
  text: string;
  tone: 'good' | 'bad' | 'neutral' | 'brown';
  impactTrend?: { text: string; tone: 'good' | 'bad' | 'brown' };
}

export interface RenderedHealthForPdf {
  title: string;
  explanation: string;
  action: string;
}

interface WeeklyReportPdfDocumentProps {
  payload: WeeklyReportPayload;
  sections: RenderedSectionForPdf[];
  health: RenderedHealthForPdf[];
  generatedAtLabel: string;
}

const BLACK  = '#0F172A';
const GRAY   = '#64748B';
const LGRAY  = '#CBD5E1';
const GREEN  = '#0F7B4E';
const RED    = '#EF4444'; // bright red — WoW impact-trend / bad tone
const BRAND  = '#0F5C8A';
const ACCENT = '#0284C7';
const BROWN  = '#78350F'; // mixed WoW comparison body

const toneColor: Record<'good' | 'bad' | 'neutral' | 'brown', string> = {
  good: GREEN,
  bad: RED,
  neutral: BLACK,
  brown: BROWN,
};

// Replace Unicode Rupee symbol with "Rs " for react-pdf built-in Helvetica font
const rs = (s: string): string => s.replace(/\u20B9\s?/g, 'Rs ');

const S = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 8.5,
    color: BLACK,
    paddingHorizontal: 28,
    paddingVertical: 18,
    position: 'relative',
    backgroundColor: '#FFFFFF',
  },

  // ─── Background Watermark (1024x199 -> 440x85.5) ──────────────────────────
  watermark: {
    position: 'absolute',
    top: '34%',
    left: '10%',
    width: 440,
    height: 85.5,
    opacity: 0.07,
    zIndex: -1,
  },

  // ─── Enterprise Header Lockup (Left Logo | Centered Title | Right Spacer) ─
  headerBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justify: 'space-between',
    borderBottomWidth: 1.5,
    borderBottomColor: BRAND,
    paddingBottom: 6,
    marginBottom: 10,
  },
  headerLogo: {
    width: 100,
    height: 66.7,
  },
  headerCenterBlock: {
    flex: 1,
    alignItems: 'center',
    justify: 'center',
  },
  headerTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 13.5,
    color: BRAND,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  headerSubtitle: {
    fontSize: 8.5,
    color: GRAY,
    marginTop: 2,
    textAlign: 'center',
  },
  headerRightSpacer: {
    width: 100,
  },

  // ─── Information Card (2-Column Grid with Secondary ID Styling) ────────────
  factBlock: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 6,
    padding: 8,
    marginBottom: 9,
  },
  factRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 3,
  },
  factLabel: {
    width: 130,
    color: GRAY,
    fontSize: 8.25,
    fontFamily: 'Helvetica-Bold',
  },
  factValueContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  factValue: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8.25,
    color: BLACK,
  },
  factValueSecondary: {
    fontFamily: 'Helvetica',
    fontSize: 8,
    color: GRAY,
    marginLeft: 4,
  },

  // ─── Section Dividers ─────────────────────────────────────────────────────
  sectionHeader: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9.5,
    color: BRAND,
    marginTop: 8,
    marginBottom: 5,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    borderBottomWidth: 0.75,
    borderBottomColor: LGRAY,
    paddingBottom: 2,
  },

  // ─── Billing Items ────────────────────────────────────────────────────────
  billItem: {
    marginBottom: 6,
    paddingLeft: 5,
    borderLeftWidth: 2,
    borderLeftColor: ACCENT,
  },
  billItemTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8.5,
    marginBottom: 2,
  },
  billItemText: {
    fontSize: 8.25,
    lineHeight: 1.35,
  },

  // ─── Health Findings ──────────────────────────────────────────────────────
  findingItem: {
    marginBottom: 6,
    borderLeftWidth: 2,
    borderLeftColor: BRAND,
    paddingLeft: 6,
  },
  findingTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8.5,
    marginBottom: 1.5,
    color: BLACK,
  },
  findingText: {
    fontSize: 8.25,
    lineHeight: 1.35,
    marginBottom: 2.5,
    color: BLACK,
  },
  actionBox: {
    backgroundColor: '#F0F6FA',
    borderRadius: 3,
    borderWidth: 0.5,
    borderColor: '#BAE6FD',
    padding: 4.5,
    marginTop: 1.5,
  },
  actionLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 7.5,
    color: BRAND,
    marginBottom: 1.5,
    textTransform: 'uppercase',
  },
  actionText: {
    fontSize: 8,
    lineHeight: 1.3,
    color: BLACK,
  },

  // ─── Footer ───────────────────────────────────────────────────────────────
  footer: {
    position: 'absolute',
    bottom: 12,
    left: 28,
    right: 28,
    fontSize: 7,
    color: GRAY,
    textAlign: 'center',
    borderTopWidth: 0.5,
    borderTopColor: LGRAY,
    paddingTop: 4,
  },
});

function renderConsumerValue(consumerStr: string) {
  const match = consumerStr.match(/^(.*?)\s*(\(Consumer ID:.*?\))$/i);
  if (match) {
    return (
      <View style={S.factValueContainer}>
        <Text style={S.factValue}>{match[1]}</Text>
        <Text style={S.factValueSecondary}>{match[2]}</Text>
      </View>
    );
  }
  return <Text style={S.factValue}>{consumerStr}</Text>;
}

export function WeeklyReportPdfDocument({
  payload,
  sections,
  health,
  generatedAtLabel,
}: WeeklyReportPdfDocumentProps) {
  return (
    <Document>
      <Page size="A4" style={S.page}>
        {/* 1. Background Watermark (Vireon Copper Logo PNG) */}
        <Image src={vireonWatermark} style={S.watermark} />

        {/* 2. Executive Header (Logo Left | Centered Title & Date | Right Spacer) */}
        <View style={S.headerBar}>
          <Image src={cortexHeaderLogo} style={S.headerLogo} />
          <View style={S.headerCenterBlock}>
            <Text style={S.headerTitle}>Weekly Impact Report</Text>
            <Text style={S.headerSubtitle}>{payload.reportWeekLabel}</Text>
          </View>
          <View style={S.headerRightSpacer} />
        </View>

        {/* 3. Facility Profile Information Card (2-Column Grid) */}
        <View style={S.factBlock}>
          <View style={S.factRow}>
            <Text style={S.factLabel}>Consumer Facility</Text>
            {renderConsumerValue(payload.facility.consumer)}
          </View>
          <View style={S.factRow}>
            <Text style={S.factLabel}>DISCOM / Tariff</Text>
            <Text style={S.factValue}>{payload.facility.discomTariff}</Text>
          </View>
          <View style={S.factRow}>
            <Text style={S.factLabel}>Sanctioned CD</Text>
            <Text style={S.factValue}>{payload.facility.contractDemandKva} kVA</Text>
          </View>
          <View style={S.factRow}>
            <Text style={S.factLabel}>Tariff Schedule</Text>
            <Text style={S.factValue}>{rs(payload.facility.tariffRatesLabel)}</Text>
          </View>
        </View>

        {/* 4. Billing & Utilisation Impact */}
        <Text style={S.sectionHeader}>Billing &amp; Utilisation Impact</Text>
        {sections.map((s) => (
          <View key={s.title} style={S.billItem}>
            <Text style={S.billItemTitle}>{rs(s.title)}</Text>
            <Text style={[S.billItemText, { color: toneColor[s.tone] }]}>
              {rs(s.text)}
            </Text>
            {s.impactTrend ? (
              <Text style={[S.billItemText, { color: toneColor[s.impactTrend.tone], marginTop: 3 }]}>
                {rs(s.impactTrend.text)}
              </Text>
            ) : null}
          </View>
        ))}

        {/* 5. Electrical Health Monitoring */}
        <Text style={S.sectionHeader}>Live Electrical Health Monitoring</Text>
        {health.length === 0 ? (
          <Text style={S.billItemText}>
            No significant electrical health findings recorded this week.
          </Text>
        ) : (
          health.map((h, i) => (
            <View key={h.title + i} style={S.findingItem}>
              <Text style={S.findingTitle}>
                {i + 1}. {rs(h.title)}
              </Text>
              <Text style={S.findingText}>{rs(h.explanation)}</Text>
              <View style={S.actionBox}>
                <Text style={S.actionLabel}>Recommended Action</Text>
                <Text style={S.actionText}>{rs(h.action)}</Text>
              </View>
            </View>
          ))
        )}

        {/* 6. Confidentiality & Verification Footer */}
        <Text style={S.footer}>
          Generated by VIREON CORTEX Energy Intelligence on {generatedAtLabel} — Telemetry figures computed directly from meter telemetry.
        </Text>
      </Page>
    </Document>
  );
}
